import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

const COMPANY_FOOTER = [
  "© Copyright 2026 | 86BIT srl Unipersonale — Codice Fiscale e P.Iva 04353030168 — Capitale sociale € 30.000,00 i.v. — Reg. Imprese di Bergamo 04353030168",
  "REA n. BG456578 — Sede Operativa: Piazza Papa Giovanni XXIII — 24020 Scanzorosciate (BG) — Tel. +39 035 310 900 — info@86bit.it",
];

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("password"); // password | twofa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setTempToken(data.temp_token);
      setStep("twofa");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", { temp_token: tempToken, code });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: brand + form + footer */}
      <div className="flex flex-col">
        <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Brand */}
        <div className="flex flex-col items-center text-center mb-10" data-testid="login-brand">
          <img src="/86bit_logo.svg" alt="86BIT" className="h-14 w-auto" />
          <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 mt-4">
            86BIT <span className="text-blue-600">Bid Flash</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1.5">Preventivi professionali con AI e firma digitale</p>
        </div>

        {/* Card */}
        <div className="w-full max-w-md border border-slate-200 rounded-2xl shadow-sm p-8" data-testid="login-card">
          {step === "password" ? (
            <>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Accedi</h2>
              <p className="text-slate-400 text-sm mt-1 mb-6">Inserisci le tue credenziali</p>
              <form onSubmit={submitPassword} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" data-testid="login-email-input"
                    value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="nome@86bit.it" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" data-testid="login-password-input"
                    value={password} onChange={(e) => setPassword(e.target.value)} required
                    placeholder="••••••••" className="mt-1.5" />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="login-submit-button"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11 rounded-lg">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Accedi
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <ShieldCheck className="w-5 h-5" />
                <span className="text-sm font-semibold uppercase tracking-wide">Verifica 2FA</span>
              </div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Codice di sicurezza</h2>
              <p className="text-slate-400 text-sm mt-1 mb-6">
                Inserisci il codice a 6 cifre della tua app di autenticazione.
              </p>
              <form onSubmit={submit2fa} className="space-y-4">
                <div>
                  <Label htmlFor="code">Codice 2FA</Label>
                  <Input id="code" inputMode="numeric" maxLength={6} data-testid="twofa-code-input"
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required
                    placeholder="123456" className="mt-1.5 text-center text-2xl tracking-[0.5em] font-mono" />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="twofa-error">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="twofa-submit-button"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11 rounded-lg">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Verifica e accedi
                </Button>
                <Button type="button" variant="ghost" className="w-full"
                  onClick={() => { setStep("password"); setCode(""); setError(""); }}>
                  Torna indietro
                </Button>
              </form>
            </>
          )}
        </div>
      </main>

      {/* Footer aziendale */}
      <footer className="border-t border-slate-100 py-6 px-6" data-testid="login-footer">
        <div className="max-w-4xl mx-auto text-center space-y-0.5">
          {COMPANY_FOOTER.map((line, i) => (
            <p key={i} className="text-[11px] leading-relaxed text-slate-400">{line}</p>
          ))}
        </div>
      </footer>
      </div>

      {/* Right: immagine d'impatto */}
      <div className="hidden lg:block relative overflow-hidden" data-testid="login-hero">
        <img
          src="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="86BIT Bid Flash"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/90 via-blue-900/55 to-blue-600/25" />
        <div className="absolute bottom-0 left-0 right-0 p-14">
          <p className="text-blue-300 font-semibold uppercase tracking-[0.25em] text-xs mb-4">86BIT Bid Flash</p>
          <h2 className="font-heading text-4xl xl:text-5xl font-black text-white leading-[1.1]">
            Preventivi che<br />chiudono trattative.
          </h2>
          <p className="mt-5 text-blue-100/90 text-base max-w-md leading-relaxed">
            Genera offerte professionali con l'AI, esporta PDF brandizzati e raccogli la firma digitale del cliente — in pochi minuti.
          </p>
        </div>
      </div>
    </div>
  );
}
